import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import {
  ConnectionProvider,
  WalletProvider,
  useWallet,
} from "@solana/wallet-adapter-react";
import { clusterApiUrl } from "@solana/web3.js";
import React, { useMemo, useEffect, useRef, useCallback } from "react";
import {
  PhantomWalletAdapter,
  SolflareWalletAdapter,
  LedgerWalletAdapter,
  TrezorWalletAdapter,
  CoinbaseWalletAdapter,
  TrustWalletAdapter
} from "@solana/wallet-adapter-wallets";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";

import "@solana/wallet-adapter-react-ui/styles.css";

export const PhantomContext = React.createContext();

const WALLET_INSTALL_URLS = {
  "Phantom": "https://phantom.app/download",
  "Solflare": "https://solflare.com/download",
  "Coinbase Wallet": "https://www.coinbase.com/wallet/downloads",
  "Trust Wallet": "https://trustwallet.com/download",
  "Trust": "https://trustwallet.com/download",
  "MetaMask": "https://metamask.io/download/",
};

function EnhancedWalletProvider({ children, wallets, endpoint, network }) {
  const handleError = useCallback((error, adapter) => {
    console.error("Wallet error:", error);
    
    if (adapter?.readyState === "NotDetected") {
      const walletName = adapter?.name || "Wallet";
      const installUrl = WALLET_INSTALL_URLS[walletName];
      
      if (installUrl) {
        const shouldOpen = window.confirm(
          `${walletName} is not installed. Would you like to install it?`
        );
        if (shouldOpen) {
          window.open(installUrl, "_blank");
        }
      }
    }
  }, []);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider 
        wallets={wallets} 
        autoConnect={false}
        onError={handleError}
      >
        <WalletModalProvider>
          <PhantomContext.Provider value={{ network, endpoint }}>
            <WalletConnectionHandler />
            {children}
          </PhantomContext.Provider>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}

function WalletConnectionHandler() {
  const { wallet, connect, connected, connecting, disconnect, select, publicKey } = useWallet();
  const isConnectingRef = useRef(false);
  const hasAttemptedRef = useRef(new Set());
  const connectionTimeoutRef = useRef(null);
  const mountedRef = useRef(true);


  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (connected && publicKey) {
      isConnectingRef.current = false;
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      console.log("Wallet connected successfully:", wallet?.adapter?.name);
    }
  }, [connected, publicKey, wallet]);

 
  useEffect(() => {
    if (!connecting && isConnectingRef.current) {
      console.log("Connecting state changed to false, resetting internal flag");
      isConnectingRef.current = false;
    }
  }, [connecting]);


  useEffect(() => {
    if (!connected && !connecting) {
      const currentWalletName = wallet?.adapter?.name;
      if (currentWalletName) {
       
        const newSet = new Set([currentWalletName]);
        hasAttemptedRef.current = newSet;
      }
    }
  }, [connected, connecting, wallet]);

 
  useEffect(() => {
   
    if (!mountedRef.current) {
      return;
    }

    if (!wallet) {
      isConnectingRef.current = false;
      return;
    }

    const walletName = wallet.adapter.name;
    const walletKey = `${walletName}-${wallet.adapter.readyState}`;

    if (connected || connecting || isConnectingRef.current) {
      return;
    }

    if (hasAttemptedRef.current.has(walletKey)) {
      return;
    }

    const attemptConnection = async () => {
   
      if (!mountedRef.current || isConnectingRef.current || connected || !wallet) {
        return;
      }

      console.log("Attempting to connect to:", walletName);

   
      const isMetaMask = walletName.toLowerCase().includes('metamask');
      
      if (isMetaMask) {
        if (!window.ethereum) {
          console.log("MetaMask not detected");
          const installUrl = WALLET_INSTALL_URLS["MetaMask"];
          const shouldInstall = window.confirm(
            "MetaMask is not installed. Would you like to install it?"
          );
          if (shouldInstall && installUrl) {
            window.open(installUrl, "_blank");
          }
          hasAttemptedRef.current.add(walletKey);
          select(null);
          return;
        }
      }

      if (wallet.adapter.readyState === "NotDetected" || 
          wallet.adapter.readyState === "Unsupported") {
        console.log(`${walletName} is not detected or unsupported`);
        
        const installUrl = WALLET_INSTALL_URLS[walletName];
        if (installUrl) {
         
            window.open(installUrl, "_blank");
       
        }
        
        hasAttemptedRef.current.add(walletKey);
        select(null);
        return;
      }

     
      isConnectingRef.current = true;
      hasAttemptedRef.current.add(walletKey);

      connectionTimeoutRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        
        console.log("Connection timeout for", walletName);
        isConnectingRef.current = false;
        
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }

       
        if (!connected) {
          console.log("Forcing wallet deselection after timeout");
          select(null);
        }
      }, 30000); 
      try {
        console.log("Calling connect for", walletName);
        await connect();
        
        if (!mountedRef.current) return;
        
        
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        isConnectingRef.current = false;
        console.log("Connection successful for", walletName);
        
      } catch (error) {
        if (!mountedRef.current) return;
        
        console.error("Connection error for", walletName, ":", error);
        
        isConnectingRef.current = false;
        
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        const isUserRejection = 
          error.message?.toLowerCase().includes("user rejected") || 
          error.message?.toLowerCase().includes("user cancelled") ||
          error.message?.toLowerCase().includes("user canceled") ||
          error.message?.toLowerCase().includes("user declined") ||
          error.message?.toLowerCase().includes("user denied") ||
          error.name === "WalletWindowClosedError" ||
          error.code === 4001 ||
          error.code === -32603;
        
        if (isUserRejection) {
          console.log("User rejected connection to", walletName);
        } else {
          console.log("Connection failed for", walletName);
        }
        
      
        select(null);
      
        try {
          if (connected) {
            await disconnect();
          }
        } catch (disconnectError) {
          console.error("Disconnect error:", disconnectError);
        }
      }
    };

    const timer = setTimeout(() => {
      attemptConnection();
    }, 100);

    return () => clearTimeout(timer);

  }, [wallet, connect, connected, connecting, disconnect, select]);

  return null;
}

function SolanaContextProvider({ children }) {
  const network = WalletAdapterNetwork.Mainnet;
  
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);
  
  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
      new LedgerWalletAdapter(),
      new TrezorWalletAdapter(),
      new CoinbaseWalletAdapter(),
      new TrustWalletAdapter(),
    ],
    [network]
  );

  return (
    <EnhancedWalletProvider 
      wallets={wallets} 
      endpoint={endpoint}
      network={network}
    >
      {children}
    </EnhancedWalletProvider>
  );
}

export { SolanaContextProvider };